import { Controller } from '@hotwired/stimulus'

// Dropdown / flyout menu. Put `data-controller="dropdown"` on a relative
// wrapper around a trigger and the menu, mark the menu with
// `data-dropdown-target="menu"` and toggle it with
// `data-action="dropdown#toggle"`. Clicking outside or pressing Escape closes
// it; clicking a menu item also closes it (add `click->dropdown#close` to the
// menu element).
export default class extends Controller {
  static targets = ['menu']

  connect() {
    this.isOpen = false
    this.handleClickOutside = this.handleClickOutside.bind(this)
    this.handleKeydown = this.handleKeydown.bind(this)
    this.handleScroll = this.handleScroll.bind(this)
  }

  disconnect() {
    this.close()
  }

  toggle() {
    this.isOpen ? this.close() : this.open()
  }

  open() {
    if (this.isOpen) return
    this.isOpen = true
    this.menuTarget.classList.remove('hidden')
    // Pin the open menu to the viewport so ancestor overflow (e.g. the
    // scrollable `.ds-table-wrap`) can't clip it. The element stays in the
    // DOM tree (so `#design-system`-scoped styles and click-outside still
    // apply); only its CSS position changes for the time it is open.
    this.menuTarget.style.position = 'fixed'
    this.position()
    document.addEventListener('click', this.handleClickOutside)
    document.addEventListener('keydown', this.handleKeydown)
    // `scroll` does not bubble, but it does fire in the capture phase, so
    // this keeps the fixed menu glued to its trigger when any scrollable
    // ancestor (including the table wrap) is scrolled while the menu is open.
    document.addEventListener('scroll', this.handleScroll, true)
    window.addEventListener('resize', this.handleScroll)
  }

  close() {
    if (!this.isOpen) return
    this.isOpen = false
    this.menuTarget.classList.add('hidden')
    this.menuTarget.style.position = ''
    document.removeEventListener('click', this.handleClickOutside)
    document.removeEventListener('keydown', this.handleKeydown)
    document.removeEventListener('scroll', this.handleScroll, true)
    window.removeEventListener('resize', this.handleScroll)
  }

  // Keep the menu inside the viewport: open below/right by default and flip
  // above or left when there isn't enough room. Positions are computed in
  // viewport coordinates because the menu is `position: fixed` while open.
  position() {
    const menu = this.menuTarget
    const wrapperRect = this.element.getBoundingClientRect()
    const menuWidth = menu.offsetWidth
    const menuHeight = menu.offsetHeight
    const viewportWidth = document.documentElement.clientWidth
    const viewportHeight = document.documentElement.clientHeight
    const gap = 8

    // Horizontal: left-align with the trigger; flip to right-aligned when it
    // would overflow the right edge of the viewport.
    let left = wrapperRect.left
    if (menuWidth > viewportWidth - wrapperRect.left && menuWidth <= wrapperRect.right) {
      left = wrapperRect.right - menuWidth
    }
    left = Math.max(gap, Math.min(left, viewportWidth - menuWidth - gap))

    // Vertical: open below; flip above when it would overflow the bottom edge.
    let top = wrapperRect.bottom + gap
    if (menuHeight + gap > viewportHeight - wrapperRect.bottom) {
      top = wrapperRect.top - menuHeight - gap
    }
    top = Math.max(gap, Math.min(top, viewportHeight - menuHeight - gap))

    menu.style.left = `${left}px`
    menu.style.top = `${top}px`
    menu.style.right = 'auto'
    menu.style.bottom = 'auto'
    menu.style.marginTop = '0'
    menu.style.marginBottom = '0'
  }

  handleScroll() {
    if (this.isOpen) this.position()
  }

  handleClickOutside(event) {
    if (!this.element.contains(event.target)) this.close()
  }

  handleKeydown(event) {
    if (event.key === 'Escape') this.close()
  }
}
